import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store/store';
import { setLoading, setSelectedLanguage } from '../../store/slices/appSlice';
import { setTokens } from '../../store/slices/authSlice';
import { envService } from '../../services/api/envService';
import { touchlessService } from '../../services/api/touchlessService';
import { useAppConfig } from '../../hooks/useAppConfig';
import { useVisitorData } from '../../hooks/useVisitorData';
import { useNewVisitorSession } from '../../hooks/useNewVisitorSession';
import { setCookie } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';
import { useStrings } from '../../hooks/useStrings';

/**
 * Primary SmartPass entry point: /sp/:uuid
 *
 * Called from Apple Wallet back-field link and QR code (both use same URL).
 * Captures visitor GPS via browser geolocation, validates against geofence,
 * then either navigates to check-in flow or sign-out page.
 */

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GEOLOCATION_UNAVAILABLE'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });
}

const SmartPassRedirect = () => {
  const S = useStrings();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { updateConfig } = useAppConfig();
  const { updateVisitorInfo, updateVisitorFullName, updateForm } = useVisitorData();
  const { startNewVisitorSession } = useNewVisitorSession();
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'generic' | 'location' | 'geofence'>('generic');

  const handleSmartPass = async () => {
    if (!token) {
      setError(S.SMARTPASS_REDIRECT.INVALID_PASS_LINK);
      return;
    }

    dispatch(setLoading(true));
    try {
      // Step 1: Capture GPS — block if unavailable or denied
      let lat: number;
      let lng: number;
      try {
        const position = await getPosition();
        lat = position.coords.latitude;
        lng = position.coords.longitude;
      } catch (geoError) {
        const err = geoError as GeolocationPositionError & { message?: string };
        if (err.message === 'GEOLOCATION_UNAVAILABLE') {
          setError(S.SMARTPASS_REDIRECT.LOCATION_REQUIRED);
          setErrorType('location');
        } else if (err.code === 1) {
          // PERMISSION_DENIED
          setError(S.SMARTPASS_REDIRECT.LOCATION_PERMISSION_REQUIRED);
          setErrorType('location');
        } else {
          setError(S.SMARTPASS_REDIRECT.LOCATION_UNAVAILABLE);
          setErrorType('location');
        }
        return;
      }

      // Store GPS for later use in visit creation payload
      sessionStorage.setItem('smartpassLat', String(lat));
      sessionStorage.setItem('smartpassLng', String(lng));

      // Step 2: Fetch check-in context with geofence validation
      const resp = await fetch(
        `${envService.apiUrl}/smartpass/checkin/by-pass/${token}?lat=${lat}&lng=${lng}`
      );

      if (resp.status === 410) {
        setError(S.SMARTPASS_REDIRECT.PASS_REVOKED_OR_EXPIRED);
        return;
      }

      if (resp.status === 403) {
        const body = await resp.json().catch(() => ({}));
        if (body.error === 'OUTSIDE_GEOFENCE') {
          setError(S.SMARTPASS_REDIRECT.OUTSIDE_GEOFENCE);
          setErrorType('geofence');
          return;
        }
      }

      if (!resp.ok) {
        throw new Error(`API returned ${resp.status}`);
      }

      const data = await resp.json();

      // Store the device JWT for subsequent API calls. setTokens also writes to
      // sessionStorage and flips isAuthenticated, keeping Redux auth state in sync
      // with the other entry points (Touchless, PreRegister).
      if (data.accessToken) {
        dispatch(setTokens({ accessToken: `Bearer ${data.accessToken}` }));
      }

      // Store SmartPass UUID for linking visit to pass
      if (data.passUuid) {
        sessionStorage.setItem('smartpassUuid', data.passUuid);
      }

      // CHECK_OUT flow: visitor is already checked in
      if (data.action === 'CHECK_OUT') {
        // Set cookies for the SignOut page
        if (data.visitId) setCookie('visitId', data.visitId, 1 / 3);
        if (data.visitorName) setCookie('visitorName', data.visitorName, 1 / 3);
        if (data.orgName) setCookie('orgName', data.orgName, 1 / 3);
        if (data.siteName) setCookie('siteName', data.siteName, 1 / 3);
        navigate('/signout', { replace: true });
        return;
      }

      // CHECK_IN flow: prepopulate visitor data and navigate to check-in
      startNewVisitorSession();

      updateVisitorInfo({
        fullName: data.visitorName || '',
        email: data.email || '',
        phoneNumber: data.phone || '',
        company: data.visitorCompany || '',
      });
      if (data.visitorName) {
        updateVisitorFullName(data.visitorName);
      }
      updateForm({
        fullName: data.visitorName || '',
        email: data.email || '',
        phone: data.phone || '',
        company: data.visitorCompany || '',
      });

      // Fetch org config using the device JWT; apply primary language if non-English
      const baseConfig = await touchlessService.getOrgConfig();
      const primaryLang = baseConfig?.language ?? 'en';
      const configResponse = primaryLang !== 'en'
        ? await touchlessService.getOrgConfig(primaryLang)
        : baseConfig;
      dispatch(setSelectedLanguage(primaryLang));
      updateConfig(configResponse);

      // Navigate to check-in flow (skip QR scan)
      const nextRoute = configResponse.privacyConsentType
        ? `/${ROUTES.PRIVACY_POLICY}`
        : `/${ROUTES.FULL_NAME}`;
      navigate(nextRoute, { replace: true });
    } catch (err) {
      console.error('SmartPass failed:', err);
      setError(`Unable to open check-in page. ${err instanceof Error ? err.message : ''}`);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    handleSmartPass();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
        <h2>{S.SMARTPASS_REDIRECT.TITLE}</h2>
        <p>{error}</p>
        {(errorType === 'location' || errorType === 'geofence') && (
          <button
            onClick={() => {
              setError('');
              setErrorType('generic');
              handleSmartPass();
            }}
            style={{
              marginTop: 16,
              padding: '12px 32px',
              fontSize: 16,
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#4F46E5',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {S.SMARTPASS_REDIRECT.BTN_RETRY}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
      <p>{S.SMARTPASS_REDIRECT.OPENING_CHECK_IN}</p>
    </div>
  );
};

export default SmartPassRedirect;
